import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Shield,
  Globe,
  FileSearch,
  Mail,
  AppWindow,
  Zap,
  Lock,
  Eye,
  BarChart3,
  Users,
  Building2,
  CheckCircle,
  ArrowRight,
  Play,
  Star,
  ChevronRight,
  Monitor,
  Cpu,
  Cloud,
  RefreshCw,
  Menu,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Logo, LogoIcon } from '../components/Logo';
import { MarketingFooter } from '../components/marketing/MarketingPageLayout';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Feature data
const protectionLayers = [
  {
    icon: Globe,
    title: 'DNS Cyber Security',
    description: 'Block malicious domains before they can connect. AI-powered filtering stops phishing, malware, and C2 servers at the network edge.',
    color: 'blue',
    stats: '99.9% threat detection'
  },
  {
    icon: FileSearch,
    title: 'File Sandbox Analysis',
    description: 'Every download is scanned in our cloud sandbox. Suspicious files are quarantined before they can execute on endpoints.',
    color: 'orange',
    stats: '<100ms scan time'
  },
  {
    icon: Mail,
    title: 'Email Security Protection',
    description: 'Advanced phishing detection with AI-powered analysis. Protect your team from BEC, spear phishing, and malicious attachments.',
    color: 'red',
    stats: '98% phishing blocked'
  },
  {
    icon: AppWindow,
    title: 'Application Control',
    description: 'Monitor and control application behavior. Detect anomalies, prevent unauthorized software, and enforce security policies.',
    color: 'purple',
    stats: 'Real-time monitoring'
  },
];

const features = [
  { icon: Cpu, title: 'AI-Powered Threat Engine', description: 'GPT-5.1 powered cyber security threat analysis provides human-readable insights and recommendations.' },
  { icon: Eye, title: 'Real-time Security Visibility', description: 'Complete visibility into all cyber security events across your organization in one dashboard.' },
  { icon: Zap, title: 'Automated Threat Response', description: 'Set up automated rules to quarantine, block, and alert without manual intervention.' },
  { icon: Cloud, title: 'Cloud-Native Security', description: 'Deploy in minutes with no hardware required. Scales automatically with your business.' },
  { icon: Lock, title: 'Zero Trust Architecture', description: 'Verify every request, never trust by default. Continuous authentication and authorization.' },
  { icon: RefreshCw, title: 'Always Updated Protection', description: 'Threat intelligence updated every 5 minutes. Stay protected against the latest cyber threats.' },
];

const testimonials = [
  {
    quote: "Clear Defense AI reduced our security incidents by 85% in the first month. The AI insights are game-changing for our cyber security posture.",
    author: "Steven Daicos",
    role: "Founder, Yellow Advances",
    avatar: "SD",
    rating: 5
  },
  {
    quote: "Finally, a cyber security platform that's actually easy to use. Our MSP manages 50+ clients effortlessly now.",
    author: "Michael Torres",
    role: "Director of Security, CloudMSP",
    avatar: "MT",
    rating: 5
  },
  {
    quote: "The multi-layer approach caught threats our previous security tools missed. Worth every penny.",
    author: "Emily Watson",
    role: "IT Manager, Nexus Corp",
    avatar: "EW",
    rating: 5
  },
];

// Pricing configuration imported from centralized config
import { PLAN_PRICES, getAnnualSavings, formatPrice } from '../config/pricing';

// Navbar Component
const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header role="banner" className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
      <nav aria-label="Main navigation" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <Logo size="default" />
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-slate-400 hover:text-white transition-colors">Features</a>
            <a href="#protection" className="text-sm text-slate-400 hover:text-white transition-colors">Protection</a>
            <a href="#pricing" className="text-sm text-slate-400 hover:text-white transition-colors">Pricing</a>
            <a href="#testimonials" className="text-sm text-slate-400 hover:text-white transition-colors">Testimonials</a>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-300 hover:text-white">
                Sign In
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Start Free Trial
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-slate-400"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden py-4 border-t border-slate-800"
          >
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-slate-400 hover:text-white">Features</a>
              <a href="#protection" className="text-slate-400 hover:text-white">Protection</a>
              <a href="#pricing" className="text-slate-400 hover:text-white">Pricing</a>
              <div className="flex gap-4 pt-4">
                <Link to="/login" className="flex-1">
                  <Button variant="outline" className="w-full border-slate-700">Sign In</Button>
                </Link>
                <Link to="/signup" className="flex-1">
                  <Button className="w-full bg-blue-600">Start Trial</Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </nav>
    </header>
  );
};

// Hero Section
const HeroSection = () => (
  <section aria-labelledby="hero-heading" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
    {/* Background */}
    <div className="absolute inset-0 bg-slate-950" aria-hidden="true">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-slate-950" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNNjAgMEgwdjYwaDYwVjB6TTEgMWg1OHY1OEgxVjF6IiBmaWxsPSIjMWUyOTNiIiBmaWxsLW9wYWNpdHk9Ii4zIi8+PC9nPjwvc3ZnPg==')] opacity-40" />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        {/* Left Column - Text */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-center lg:text-left"
        >
          <motion.div variants={fadeInUp}>
            <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 mb-6">
              Powered by GPT-5.1 AI
            </Badge>
          </motion.div>
          
          <motion.h1 
            id="hero-heading"
            variants={fadeInUp}
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight"
          >
            Multi-Layer
            <span className="block bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
              Cyber Security
            </span>
            for Modern Business
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="mt-6 text-lg text-slate-400 max-w-xl mx-auto lg:mx-0"
          >
            Protect your organization with unified DNS filtering, file sandboxing, email security, and application control—all powered by AI-driven cyber security and managed from a single dashboard.
          </motion.p>

          <motion.div 
            variants={fadeInUp}
            className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
          >
            <Link to="/signup">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8 w-full sm:w-auto">
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 text-lg px-8" aria-label="Watch product demo video">
              <Play className="mr-2 w-5 h-5" />
              Watch Demo
            </Button>
          </motion.div>

          <motion.div 
            variants={fadeInUp}
            className="mt-8 flex items-center gap-6 justify-center lg:justify-start"
          >
            <div className="flex -space-x-2" aria-hidden="true">
              {['SC', 'MT', 'EW', 'JD'].map((initials, i) => (
                <div key={i} className="w-10 h-10 rounded-full bg-slate-800 border-2 border-slate-950 flex items-center justify-center text-xs font-medium text-slate-300">
                  {initials}
                </div>
              ))}
            </div>
            <div className="text-left">
              <div className="flex items-center gap-1" aria-label="5 out of 5 stars rating">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" aria-hidden="true" />)}
              </div>
              <p className="text-sm text-slate-400">Trusted by 500+ companies</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Right Column - Dashboard Preview */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative"
        >
          <figure className="relative rounded-2xl overflow-hidden border border-slate-800 shadow-2xl shadow-blue-500/10">
            <img 
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
              alt="Clear Defense AI cyber security dashboard showing real-time threat monitoring, security analytics, and protection status"
              className="w-full h-auto"
              loading="lazy"
              width="800"
              height="450"
            />
            {/* Overlay with fake UI elements */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" aria-hidden="true" />
            <figcaption className="absolute bottom-4 left-4 right-4">
              <div className="bg-slate-900/90 backdrop-blur-sm rounded-lg p-4 border border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-emerald-500" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">All Systems Protected</p>
                    <p className="text-xs text-slate-400">Last threat blocked 2 minutes ago</p>
                  </div>
                  <Badge className="ml-auto bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                    Active
                  </Badge>
                </div>
              </div>
            </figcaption>
          </figure>
          
          {/* Floating Stats Cards */}
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="absolute -left-8 top-1/4 bg-slate-900/90 backdrop-blur-sm rounded-lg p-4 border border-slate-800 shadow-xl hidden lg:block"
            aria-label="Cyber security threats blocked statistic"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-500" aria-hidden="true" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">2.4M</p>
                <p className="text-xs text-slate-400">Threats Blocked</p>
              </div>
            </div>
          </motion.aside>

          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="absolute -right-8 bottom-1/4 bg-slate-900/90 backdrop-blur-sm rounded-lg p-4 border border-slate-800 shadow-xl hidden lg:block"
            aria-label="Devices protected statistic"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Monitor className="w-5 h-5 text-blue-500" aria-hidden="true" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">50K+</p>
                <p className="text-xs text-slate-400">Devices Protected</p>
              </div>
            </div>
          </motion.aside>
        </motion.div>
      </div>

      {/* Trusted By Logos */}
      <motion.aside 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="mt-20 text-center"
        aria-label="Trusted by leading companies"
      >
        <p className="text-sm text-slate-500 mb-6">TRUSTED BY LEADING COMPANIES FOR CYBER SECURITY</p>
        <div className="flex flex-wrap items-center justify-center gap-8 opacity-50">
          {['Thomko', 'Yellow Advances', 'Capital Stack', 'SecureNet'].map((company) => (
            <span key={company} className="text-xl font-heading font-bold text-slate-600">
              {company}
            </span>
          ))}
        </div>
      </motion.aside>
    </div>
  </section>
);

// Protection Layers Section
const ProtectionSection = () => (
  <section id="protection" aria-labelledby="protection-heading" className="py-24 bg-slate-950 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/50 to-slate-950" aria-hidden="true" />
    
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
        className="text-center mb-16"
      >
        <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 mb-4">
          4 Layers of Cyber Security Protection
        </Badge>
        <h2 id="protection-heading" className="font-heading font-bold text-3xl sm:text-4xl text-white">
          Complete Cyber Security Coverage
        </h2>
        <p className="mt-4 text-lg text-slate-400 max-w-2xl mx-auto">
          Protect every attack surface with our comprehensive multi-layer cyber security approach
        </p>
      </motion.header>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        role="list"
        aria-label="Cyber security protection layers"
      >
        {protectionLayers.map((layer, index) => {
          const colorClasses = {
            blue: 'bg-blue-500/10 text-blue-500 border-blue-500/20 group-hover:bg-blue-500/20',
            orange: 'bg-orange-500/10 text-orange-500 border-orange-500/20 group-hover:bg-orange-500/20',
            red: 'bg-red-500/10 text-red-500 border-red-500/20 group-hover:bg-red-500/20',
            purple: 'bg-purple-500/10 text-purple-500 border-purple-500/20 group-hover:bg-purple-500/20',
          };

          return (
            <motion.article key={index} variants={fadeInUp} role="listitem">
              <Card className="group bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all duration-300 h-full hover:shadow-xl hover:shadow-blue-500/5">
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-xl ${colorClasses[layer.color]} flex items-center justify-center mb-4 transition-colors`}>
                    <layer.icon className="w-7 h-7" aria-hidden="true" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-white mb-2">{layer.title}</h3>
                  <p className="text-slate-400 text-sm mb-4">{layer.description}</p>
                  <Badge variant="outline" className="border-slate-700 text-slate-400">
                    {layer.stats}
                  </Badge>
                </CardContent>
              </Card>
            </motion.article>
          );
        })}
      </motion.div>
    </div>
  </section>
);

// Features Section
const FeaturesSection = () => (
  <section id="features" aria-labelledby="features-heading" className="py-24 bg-slate-900 relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        {/* Left - Image */}
        <motion.figure
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="relative pb-8 sm:pb-0"
        >
          <div className="rounded-2xl overflow-hidden border border-slate-800">
            <img 
              src="https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80"
              alt="AI-powered cyber security analysis interface showing threat detection, malware scanning, and security intelligence features"
              className="w-full h-auto"
              loading="lazy"
              width="800"
              height="533"
            />
          </div>
          {/* AI Badge */}
          <figcaption className="absolute -bottom-4 right-2 sm:-bottom-6 sm:-right-6 bg-slate-900 rounded-xl p-3 sm:p-4 border border-slate-800 shadow-xl max-w-[calc(100%-1rem)] sm:max-w-none">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <Cpu className="w-5 h-5 sm:w-6 sm:h-6 text-white" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-white truncate">AI-Powered Cyber Security</p>
                <p className="text-xs text-slate-400">GPT-5.1 Analysis</p>
              </div>
            </div>
          </figcaption>
        </motion.figure>

        {/* Right - Features */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.header variants={fadeInUp}>
            <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 mb-4">
              Platform Features
            </Badge>
            <h2 id="features-heading" className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
              Built for Modern Cyber Security Teams
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Everything you need to protect your organization, powered by cutting-edge AI cyber security technology.
            </p>
          </motion.header>

          <div className="grid sm:grid-cols-2 gap-6" role="list" aria-label="Cyber security platform features">
            {features.map((feature, index) => (
              <motion.article
                key={index}
                variants={fadeInUp}
                className="flex items-start gap-4"
                role="listitem"
              >
                <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-blue-500" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-medium text-white mb-1">{feature.title}</h3>
                  <p className="text-sm text-slate-400">{feature.description}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Testimonials Section
const TestimonialsSection = () => (
  <section id="testimonials" aria-labelledby="testimonials-heading" className="py-24 bg-slate-950 relative">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
        className="text-center mb-16"
      >
        <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 mb-4">
          Customer Testimonials
        </Badge>
        <h2 id="testimonials-heading" className="font-heading font-bold text-3xl sm:text-4xl text-white">
          Loved by Cyber Security Teams
        </h2>
        <p className="mt-4 text-lg text-slate-400 max-w-2xl mx-auto">
          See what our customers have to say about Clear Defense AI cyber security platform
        </p>
      </motion.header>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="grid md:grid-cols-3 gap-6"
        role="list"
        aria-label="Customer testimonials"
      >
        {testimonials.map((testimonial, index) => (
          <motion.article key={index} variants={fadeInUp} role="listitem">
            <Card className="bg-slate-900/50 border-slate-800 h-full">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-4" aria-label={`${testimonial.rating} out of 5 stars`}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="text-slate-300 mb-6">&ldquo;{testimonial.quote}&rdquo;</blockquote>
                <footer className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-sm font-medium text-slate-300" aria-hidden="true">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <cite className="font-medium text-white not-italic">{testimonial.author}</cite>
                    <p className="text-sm text-slate-400">{testimonial.role}</p>
                  </div>
                </footer>
              </CardContent>
            </Card>
          </motion.article>
        ))}
      </motion.div>
    </div>
  </section>
);

// Pricing Section
const PricingSection = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  
  const plans = [
    { key: 'professional', popular: false },
    { key: 'enterprise', popular: true },
  ];

  return (
    <section id="pricing" aria-labelledby="pricing-heading" className="py-24 bg-slate-900 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.header
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="text-center mb-16"
        >
          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 mb-4">
            Simple Cyber Security Pricing
          </Badge>
          <h2 id="pricing-heading" className="font-heading font-bold text-3xl sm:text-4xl text-white">
            Choose Your Cyber Security Plan
          </h2>
          <p className="mt-4 text-lg text-slate-400 max-w-2xl mx-auto">
            Start with a 14-day free trial. No credit card required.
          </p>
          
          {/* Billing Toggle */}
          <div className="mt-8 flex items-center justify-center gap-4">
            <span className={`text-sm font-medium transition-colors ${billingCycle === 'monthly' ? 'text-white' : 'text-slate-400'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              className={`relative w-16 h-8 rounded-full transition-all duration-300 ${
                billingCycle === 'annual' ? 'bg-emerald-600' : 'bg-slate-600'
              }`}
              aria-label={`Switch to ${billingCycle === 'monthly' ? 'annual' : 'monthly'} billing`}
              role="switch"
              aria-checked={billingCycle === 'annual'}
            >
              <span 
                className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ease-in-out ${
                  billingCycle === 'annual' ? 'translate-x-8' : 'translate-x-0'
                }`} 
                aria-hidden="true"
              />
            </button>
            <span className={`text-sm font-medium transition-colors ${billingCycle === 'annual' ? 'text-white' : 'text-slate-400'}`}>
              Annual
            </span>
            {billingCycle === 'annual' && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 ml-2">
                Save 2 months!
              </Badge>
            )}
          </div>
        </motion.header>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          role="list"
          aria-label="Cyber security pricing plans"
        >
          {plans.map((planInfo, index) => {
            const plan = PLAN_PRICES[planInfo.key];
            const price = billingCycle === 'annual' 
              ? Math.round(plan.annual / 12) 
              : plan.monthly;
            const savings = getAnnualSavings(planInfo.key);
            
            return (
              <motion.article key={index} variants={fadeInUp} role="listitem">
                <Card className={`relative h-full ${
                  planInfo.popular 
                    ? 'bg-gradient-to-b from-blue-500/10 to-slate-900 border-blue-500/50' 
                    : 'bg-slate-900/50 border-slate-800'
                }`}>
                  {planInfo.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-blue-600 text-white border-0">Most Popular</Badge>
                    </div>
                  )}
                  <CardContent className="p-8">
                    <h3 className="font-heading font-semibold text-xl text-white mb-2">{plan.name}</h3>
                    <p className="text-slate-400 text-sm mb-6">{plan.description}</p>
                    <div className="mb-2">
                      <span className="text-4xl font-heading font-bold text-white">{formatPrice(price)}</span>
                      <span className="text-slate-400">/month</span>
                    </div>
                    {billingCycle === 'annual' && (
                      <div className="mb-4">
                        <span className="text-sm text-slate-500 line-through mr-2">
                          {formatPrice(plan.monthly)}/mo
                        </span>
                        <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
                          Save {formatPrice(savings)}/year
                        </Badge>
                      </div>
                    )}
                    {billingCycle === 'monthly' && <div className="mb-4 h-6" />}
                    <Link to="/signup">
                      <Button 
                        className={`w-full mb-6 ${
                          planInfo.popular 
                            ? 'bg-blue-600 hover:bg-blue-700' 
                            : 'bg-slate-800 hover:bg-slate-700'
                        }`}
                      >
                        Start Free Trial
                      </Button>
                    </Link>
                    <ul className="space-y-3" aria-label={`${plan.name} features`}>
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-3 text-sm text-slate-300">
                          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" aria-hidden="true" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    {billingCycle === 'annual' && (
                      <p className="mt-6 text-xs text-slate-500 text-center">
                        Billed annually at {formatPrice(plan.annual)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.article>
            );
          })}
        </motion.div>
        
        {/* Additional pricing info */}
        <motion.footer
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mt-12 text-center"
        >
          <p className="text-slate-400 text-sm">
            Need more users? Additional seats available at $29/user/month (Professional) or $25/user/month (Enterprise).
          </p>
          <p className="text-slate-500 text-xs mt-2">
            Flexible billing: Pay annually for your cyber security plan and monthly for additional users.
          </p>
        </motion.footer>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section aria-labelledby="cta-heading" className="py-24 bg-slate-950 relative overflow-hidden">
    <div className="absolute inset-0" aria-hidden="true">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-3xl" />
    </div>
    
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeIn}
      className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
    >
      <h2 id="cta-heading" className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
        Ready to Secure Your Business with AI Cyber Security?
      </h2>
      <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
        Join 500+ companies protecting their organizations with Clear Defense AI cyber security platform. Start your free trial today.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/signup">
          <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8">
            Start Free Trial
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
        <Link to="/contact">
          <Button size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 text-lg px-8">
            Contact Sales
          </Button>
        </Link>
      </div>
    </motion.div>
  </section>
);

// Main Landing Page Component
export default function Landing() {
  return (
    <main className="min-h-screen bg-slate-950">
      <Navbar />
      <HeroSection />
      <ProtectionSection />
      <FeaturesSection />
      <TestimonialsSection />
      <PricingSection />
      <CTASection />
      <MarketingFooter />
    </main>
  );
}
